import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Custom "A" flame icon - combines flame shape with letter A silhouette
const FlameAIcon = ({ className = "w-6 h-6", fill = "white" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Flame shaped like letter A */}
    <path
      d="M12 2C12 2 8 6 8 11C8 12.5 8.5 14 9.5 15L10.5 12L12 16L13.5 12L14.5 15C15.5 14 16 12.5 16 11C16 6 12 2 12 2Z"
      fillRule="evenodd"
    />
    {/* Inner flame creating A shape */}
    <path
      d="M12 5C12 5 9.5 8 9.5 11.5C9.5 13 10 14.5 11 15.5L12 13L13 15.5C14 14.5 14.5 13 14.5 11.5C14.5 8 12 5 12 5Z"
      fill="rgba(0,0,0,0.2)"
    />
    {/* Horizontal bar of A */}
    <rect x="9.5" y="13" width="5" height="1.5" rx="0.75" fill="rgba(0,0,0,0.3)" />
    {/* Base flames */}
    <path
      d="M8 18C8 18 9 16 10 16.5C11 17 11.5 19 12 19C12.5 19 13 17 14 16.5C15 16 16 18 16 18C16 20 14 22 12 22C10 22 8 20 8 18Z"
      fillRule="evenodd"
    />
  </svg>
);

// Alternative cleaner A-flame design
const FlameAIconClean = ({ className = "w-6 h-6", fill = "white" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Main A-shaped flame */}
    <path
      d="M12 1C12 1 6 8 6 14C6 16.5 7 18.5 8.5 20L10 15H14L15.5 20C17 18.5 18 16.5 18 14C18 8 12 1 12 1ZM10.5 13L12 7L13.5 13H10.5Z"
      fillRule="evenodd"
    />
  </svg>
);

// Modern minimalist A-flame
const FlameAIconModern = ({ className = "w-6 h-6", fill = "white" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Outer flame shape forming A */}
    <path
      d="M12 2L6 16L8 20H10L11 17H13L14 20H16L18 16L12 2ZM11 14L12 8L13 14H11Z"
    />
    {/* Flame tips at top */}
    <path
      d="M12 2C12 2 10 4 10 6C10 7 11 8 12 8C13 8 14 7 14 6C14 4 12 2 12 2Z"
      opacity="0.8"
    />
  </svg>
);

// Final polished icon - Two flame-arrows representing exchange/swap
const AltalabIcon = ({ className = "w-6 h-6" }) => (
  <svg
    viewBox="0 0 32 32"
    className={className}
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Top flame-arrow going RIGHT → */}
    <path
      d="M4 8C4 8 8 6 12 8C14 9 15 11 15 11L15 8L22 12L15 16L15 13C15 13 14 15 12 16C8 18 4 16 4 16C4 16 7 14 7 12C7 10 4 8 4 8Z"
    />
    {/* Bottom flame-arrow going LEFT ← */}
    <path
      d="M28 24C28 24 24 26 20 24C18 23 17 21 17 21L17 24L10 20L17 16L17 19C17 19 18 17 20 16C24 14 28 16 28 16C28 16 25 18 25 20C25 22 28 24 28 24Z"
    />
  </svg>
);

// Logo component with text
const AltalabLogo = ({ size = "default", showText = true, clickable = true }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const sizes = {
    small: { container: "w-8 h-8", icon: "w-5 h-5", text: "text-lg" },
    default: { container: "w-10 h-10", icon: "w-6 h-6", text: "text-2xl" },
    large: { container: "w-12 h-12", icon: "w-7 h-7", text: "text-3xl" },
  };
  const s = sizes[size] || sizes.default;

  const handleClick = () => {
    if (!clickable) return;
    
    if (token) {
      // If logged in, go to discovery
      navigate("/discovery");
    } else {
      // If not logged in, go to home and refresh
      window.location.href = "/";
    }
  };

  return (
    <div 
      className={`flex items-center gap-2 ${clickable ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      <div
        className={`${s.container} rounded-xl bg-gradient-to-br from-[#FD5564] to-[#FF7854] flex items-center justify-center shadow-lg`}
      >
        <AltalabIcon className={s.icon} />
      </div>
      {showText && (
        <span
          className={`font-heading font-bold ${s.text} tracking-tight bg-gradient-to-r from-[#FD5564] to-[#FF7854] bg-clip-text text-transparent`}
        >
          altalab
        </span>
      )}
    </div>
  );
};

export { AltalabLogo, AltalabIcon };
export default AltalabLogo;
